import React from "react";
import { motion } from "framer-motion";
import { staggerContainer, cardItem } from "lib/animations";
import { useInView } from "lib/hooks/useInView";
import { useCountUp } from "lib/hooks/useCountUp";
import OptimizedImage from "components/OptimizedImage";

interface Stat {
  value: string;
  title: string;
  countTo?: number;
  suffix?: string;
  image: string;
}

function CounterStat({ stat, started }: { stat: Stat; started: boolean }) {
  const count = useCountUp(stat.countTo ?? 0, 1800, started && stat.countTo !== undefined);
  const display = stat.countTo !== undefined ? `${count.toLocaleString()}${stat.suffix ?? ""}` : stat.value;
  return (
    <span
      className="text-3xl md:text-4xl font-black text-primary-600 leading-none tracking-tight"
      style={{ fontVariantNumeric: "tabular-nums" }}
    >
      {display}
    </span>
  );
}

const StatsStrip: React.FC = () => {
  const [ref, inView] = useInView<HTMLDivElement>(0.1);

  const stats: Stat[] = [
    { value: "Active", title: "Live Classes Daily", image: "/img/goalfeature/image1.webp" },
    { value: "98%", title: "Success Rate", countTo: 98, suffix: "%", image: "/img/goalfeature/image2.webp" },
    { value: "100K+", title: "Learners", countTo: 100, suffix: "K+", image: "/img/goalfeature/image3.webp" },
    { value: "Top 1%", title: "Rank Achievers", image: "/img/goalfeature/image4.webp" },
    { value: "24/7", title: "Learning Support", image: "/img/goalfeature/image5.webp" },
    { value: "500+", title: "Mock Tests", countTo: 500, suffix: "+", image: "/img/goalfeature/image6.webp" },
  ];

  return (
    <section ref={ref} className="relative z-20 px-4 -mt-16 md:-mt-20">
      <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100 p-2 sm:p-4">
        <motion.div
          className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2 sm:gap-4"
          variants={staggerContainer}
          initial="hidden"
          animate={inView ? "show" : "hidden"}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              variants={cardItem}
              whileHover={{ scale: 1.05, y: -2 }}
              className="flex flex-col items-center text-center justify-center gap-1.5 p-4 md:p-6 cursor-default"
            >
              <OptimizedImage
                src={stat.image}
                alt={stat.title}
                width={32}
                height={32}
                className="w-8 h-8 mb-1 object-contain"
              />
              <CounterStat stat={stat} started={inView} />
              <p className="text-xs font-bold uppercase tracking-wider text-gray-700 mt-1">
                {stat.title}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default StatsStrip;
